import { Suspense, lazy, memo } from "react";
import { Skeleton, SvgIconOwnProps } from "@mui/material";

interface LazyIconProps extends SvgIconOwnProps {
  name: string;
}

const iconMap = {
  Event: lazy(() => import("@mui/icons-material/Event")),
  House: lazy(() => import("@mui/icons-material/House")),
  Leaderboard: lazy(() => import("@mui/icons-material/Leaderboard")),
  LocationOn: lazy(() => import("@mui/icons-material/LocationOn")),
  Search: lazy(() => import("@mui/icons-material/Search")),
  TableChart: lazy(() => import("@mui/icons-material/TableChart")),
  Image: lazy(() => import("@mui/icons-material/Image")),
  ElectricBolt: lazy(() => import("@mui/icons-material/ElectricBolt")),
  AttachMoney: lazy(() => import("@mui/icons-material/AttachMoney")),
  BarChart: lazy(() => import("@mui/icons-material/BarChart")),
  Business: lazy(() => import("@mui/icons-material/Business")),
  DataUsage: lazy(() => import("@mui/icons-material/DataUsage")),
  Description: lazy(() => import("@mui/icons-material/Description")),
  Domain: lazy(() => import("@mui/icons-material/Domain")),
  EmojiEventsOutlined: lazy(() => import("@mui/icons-material/EmojiEventsOutlined")),
  HandshakeOutlined: lazy(() => import("@mui/icons-material/HandshakeOutlined")),
  InsertChart: lazy(() => import("@mui/icons-material/InsertChart")),
  MultilineChart: lazy(() => import("@mui/icons-material/MultilineChart")),
  PieChart: lazy(() => import("@mui/icons-material/PieChart")),
  ShowChart: lazy(() => import("@mui/icons-material/ShowChart")),
  Timeline: lazy(() => import("@mui/icons-material/Timeline")),
  AutoGraph: lazy(() => import("@mui/icons-material/AutoGraph")),
  School: lazy(() => import("@mui/icons-material/School")),
  PhoneIphone: lazy(() => import("@mui/icons-material/PhoneIphone")),
  VillaOutlined: lazy(() => import("@mui/icons-material/VillaOutlined")),
  PeopleOutlined: lazy(() => import("@mui/icons-material/PeopleOutlined")),
  CampaignOutlined: lazy(() => import("@mui/icons-material/CampaignOutlined")),
  ConnectWithoutContactOutlined: lazy(
    () => import("@mui/icons-material/ConnectWithoutContactOutlined"),
  ),
  PersonSearchOutlined: lazy(() => import("@mui/icons-material/PersonSearchOutlined")),
  RestoreOutlined: lazy(() => import("@mui/icons-material/RestoreOutlined")),
  AccountTreeOutlined: lazy(() => import("@mui/icons-material/AccountTreeOutlined")),
  HubOutlined: lazy(() => import("@mui/icons-material/HubOutlined")),
  EventNoteOutlined: lazy(() => import("@mui/icons-material/EventNoteOutlined")),
  RecordVoiceOverOutlined: lazy(
    () => import("@mui/icons-material/RecordVoiceOverOutlined"),
  ),
  ShieldOutlined: lazy(() => import("@mui/icons-material/ShieldOutlined")),
  WorkspacePremiumOutlined: lazy(
    () => import("@mui/icons-material/WorkspacePremiumOutlined"),
  ),
  MarkEmailReadOutlined: lazy(
    () => import("@mui/icons-material/MarkEmailReadOutlined"),
  ),
  HomeWorkOutlined: lazy(() => import("@mui/icons-material/HomeWorkOutlined")),
  EditNoteOutlined: lazy(() => import("@mui/icons-material/EditNoteOutlined")),
  AdsClickOutlined: lazy(() => import("@mui/icons-material/AdsClickOutlined")),
  InsightsOutlined: lazy(() => import("@mui/icons-material/InsightsOutlined")),
  GavelOutlined: lazy(() => import("@mui/icons-material/GavelOutlined")),
  BalanceOutlined: lazy(() => import("@mui/icons-material/BalanceOutlined")),
  CelebrationOutlined: lazy(() => import("@mui/icons-material/CelebrationOutlined")),
  ForumOutlined: lazy(() => import("@mui/icons-material/ForumOutlined")),
  GroupAddOutlined: lazy(() => import("@mui/icons-material/GroupAddOutlined")),
  GroupsOutlined: lazy(() => import("@mui/icons-material/GroupsOutlined")),
  LeaderboardOutlined: lazy(() => import("@mui/icons-material/LeaderboardOutlined")),
  ListAltOutlined: lazy(() => import("@mui/icons-material/ListAltOutlined")),
  LockOpenOutlined: lazy(() => import("@mui/icons-material/LockOpenOutlined")),
  MapOutlined: lazy(() => import("@mui/icons-material/MapOutlined")),
  PolicyOutlined: lazy(() => import("@mui/icons-material/PolicyOutlined")),
  RocketLaunchOutlined: lazy(() => import("@mui/icons-material/RocketLaunchOutlined")),
  SettingsSuggestOutlined: lazy(
    () => import("@mui/icons-material/SettingsSuggestOutlined"),
  ),
  TrendingUpOutlined: lazy(() => import("@mui/icons-material/TrendingUpOutlined")),
  ElectricBoltOutlined: lazy(() => import("@mui/icons-material/ElectricBoltOutlined")),
  TimelineOutlined: lazy(() => import("@mui/icons-material/TimelineOutlined")),
  VolunteerActivismOutlined: lazy(
    () => import("@mui/icons-material/VolunteerActivismOutlined"),
  ),
  VpnKeyOutlined: lazy(() => import("@mui/icons-material/VpnKeyOutlined")),
  WorkOutlineOutlined: lazy(
    () => import("@mui/icons-material/WorkOutlineOutlined"),
  ),
  DocumentScannerOutlined: lazy(
    () => import("@mui/icons-material/DocumentScannerOutlined"),
  ),
} as const;

type IconName = keyof typeof iconMap;

const warnedMissing = new Set<string>();

const getIcon = (name: LazyIconProps["name"]) => {
  const icon = iconMap[name as IconName];

  if (!icon) {
    if (import.meta.env.DEV && name && !warnedMissing.has(name)) {
      warnedMissing.add(name);
      console.warn(`[SvgIcon] Missing icon mapping for: "${name}"`);
    }
    return iconMap.ElectricBolt;
  }

  return icon;
};

export const SvgIcon = memo(function Component({
  name,
  ...rest
}: LazyIconProps) {
  const IconComponent = getIcon(name);

  return (
    <Suspense fallback={<Skeleton variant="rounded" height={24} width={24} />}>
      <IconComponent {...rest} />
    </Suspense>
  );
});
